import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  PencilSquareIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useState, useMemo } from "react";
import { Text } from "@core/text";
import { useGetMaarifLearningOutcomes } from "@packages/api/madlenAPI";
import {
  GetTopicsCourseType,
  LearningOutcomeDto,
} from "@packages/api/madlenAPI.schemas";
import Masonry from "react-masonry-css";

interface UnitData {
  alanBecerileri?: string;
  anahtarKavramlar?: string;
  becerilerArasiIliskiler?: string;
  degerler?: string;
  destekleme?: string;
  disiplinlerArasiIliskiler?: string;
  egilimler?: string;
  icerikCercevesiList?: string[];
  kavramsalBeceriler?: string;
  kopruKurma?: string;
  ogrenmeKanitlariOlcmeVeDegerlendirme?: string;
  ogrenmeOgretmeUygulamalariList?: string[];
  okuryazarlikBecerileri?: string;
  onDegerlendirmeSureci?: string;
  sosyalDuygusalOgrenmeBecerileri?: string;
  temelKabuller?: string;
  zenginlestirme?: string;
}

interface CrossProgramStepProps {
  customInput?: string;
  onCustomInputChange: (value: string) => void;
  learningOutcomes?: LearningOutcomeDto[];
  grade: number;
  courseType: GetTopicsCourseType;
}

const MAX_CHARS = 500;

const CrossProgramStep: React.FC<CrossProgramStepProps> = ({
  customInput = "",
  onCustomInputChange,
  learningOutcomes = [],
  grade,
  courseType,
}) => {
  const { t } = useTranslation();
  const [showCharacterWarning, setShowCharacterWarning] = useState(false);

  const { data: maarifOutcomesData } = useGetMaarifLearningOutcomes({
    grade,
    courseType,
  });

  const selectedUnits = useMemo(() => {
    return maarifOutcomesData?.units?.filter((unit) =>
      unit.outcomes?.find((outcome) =>
        learningOutcomes.map((a) => a.id).includes(outcome.id!.toString()),
      ),
    );
  }, [maarifOutcomesData, learningOutcomes]);

  // Function to combine column data from all units
  const getCombinedColumnData = (columnKey: keyof UnitData) => {
    if (!selectedUnits?.length) return [];

    return selectedUnits
      .reduce<(string | undefined)[]>((acc, unit) => {
        const value = unit[columnKey];
        if (Array.isArray(value)) {
          return [...acc, ...value];
        }
        if (value) {
          return [...acc, value];
        }
        return acc;
      }, [])
      .filter(Boolean);
  };

  // Column definitions with their display names
  const columns: { key: keyof UnitData; title: string }[] = [
    { key: "alanBecerileri", title: t("crossProgram.columns.alanBecerileri") },
    {
      key: "anahtarKavramlar",
      title: t("crossProgram.columns.anahtarKavramlar"),
    },
    {
      key: "becerilerArasiIliskiler",
      title: t("crossProgram.columns.becerilerArasiIliskiler"),
    },
    { key: "degerler", title: t("crossProgram.columns.degerler") },
    { key: "destekleme", title: t("crossProgram.columns.destekleme") },
    {
      key: "disiplinlerArasiIliskiler",
      title: t("crossProgram.columns.disiplinlerArasiIliskiler"),
    },
    { key: "egilimler", title: t("crossProgram.columns.egilimler") },
    {
      key: "kavramsalBeceriler",
      title: t("crossProgram.columns.kavramsalBeceriler"),
    },
    {
      key: "okuryazarlikBecerileri",
      title: t("crossProgram.columns.okuryazarlikBecerileri"),
    },
    {
      key: "onDegerlendirmeSureci",
      title: t("crossProgram.columns.onDegerlendirmeSureci"),
    },
    {
      key: "sosyalDuygusalOgrenmeBecerileri",
      title: t("crossProgram.columns.sosyalDuygusalOgrenmeBecerileri"),
    },
    { key: "temelKabuller", title: t("crossProgram.columns.temelKabuller") },
    { key: "kopruKurma", title: t("crossProgram.columns.kopruKurma") },
    {
      key: "ogrenmeKanitlariOlcmeVeDegerlendirme",
      title: t("crossProgram.columns.ogrenmeKanitlariOlcmeVeDegerlendirme"),
    },
    { key: "zenginlestirme", title: t("crossProgram.columns.zenginlestirme") },
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Column Cards Grid */}
        <Masonry
          breakpointCols={{
            1536: 2,
            1024: 2,
            768: 1,
          }}
          className="flex gap-3"
        >
          {columns.map((column) => {
            const columnData = getCombinedColumnData(column.key);
            if (columnData.length === 0) return null;

            return (
              <motion.div
                key={column.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 my-4 rounded-xl border border-emerald-500 dark:border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30"
              >
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {column.title}
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-green-50 [&::-webkit-scrollbar-thumb]:bg-green-400 dark:[&::-webkit-scrollbar-track]:bg-gray-700 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full">
                  {columnData.map((item, index) => (
                    <Text key={index} variant="p" status="secondary" size="sm">
                      {item}
                    </Text>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </Masonry>

        {/* Custom Input */}
        <div className="relative">
          <input
            type="text"
            value={customInput}
            onChange={(e) => {
              onCustomInputChange(e.target.value);
              setShowCharacterWarning(e.target.value.length > MAX_CHARS * 0.9);
            }}
            className={`w-full p-4 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 ${
              showCharacterWarning
                ? "border-yellow-300 dark:border-yellow-600"
                : "border-gray-200 dark:border-gray-700"
            }`}
            placeholder={t("crossProgram.customInputPlaceholder")}
          />
          <div className="absolute bottom-3 right-3 flex items-center space-x-2">
            <Text
              variant="span"
              size="sm"
              className={`${
                showCharacterWarning && "text-yellow-600 dark:text-yellow-400"
              }`}
            >
              {customInput.length}/500
            </Text>
            <PencilSquareIcon className="w-5 h-5 text-gray-400" />
          </div>
        </div>

        {/* Helper Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                {t("crossProgram.helper.title")}
              </h3>
              <Text className="mt-2" variant="p" status="success" size="sm">
                {t("crossProgram.helper.description")}
              </Text>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CrossProgramStep;
